import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/app/providers";
import { useCart } from "@/features/cart";
import { apiService } from "@/shared/api";
import { useSecureSSE } from "@/shared/hooks";
import {
  PAYMENT_ENDPOINTS,
  CART_ENDPOINTS,
  type PaymentState,
  type CreatePaymentResponse,
} from "@/constants/api.payment";

export const usePayment = () => {
  const { token, currentBuilding } = useAuth();
  const { state: cartState, clear: clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState<PaymentState>({
    status: "idle",
  });
  const cartIdRef = useRef<number | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Popup monitoring cleanup
  const stopPopupMonitoring = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    popupRef.current = null;
  }, []);
  
  // Centralized status update handler - stable reference
  const handleStatusUpdate = useCallback((status: string, previousStatus?: string) => {
    console.log(`🔄 Payment status update: ${previousStatus || 'unknown'} → ${status}`);
    
    if (status === "PAYÉ") {
      console.log("✅ Payment confirmed as PAID");
      setPaymentStatus({ status: "success" });
      clearCart();
      stopPopupMonitoring();
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    } else if (status === "REFUSÉ") {
      console.log("❌ Payment confirmed as REFUSED");
      setPaymentStatus({ status: "failed", error: "Paiement refusé" });
      clearCart();
      stopPopupMonitoring();
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    } else {
      console.log("⏳ Payment still processing, status:", status);
    }
  }, [clearCart, stopPopupMonitoring]);
  
  // Stable error handler
  const handleSSEError = useCallback((error: string) => {
    console.warn('🚨 SSE error, will use URL detection as fallback:', error);
  }, []);
  
  // Stable timeout handler
  const handleSSETimeout = useCallback(() => {
    console.log('⏰ SSE timeout, connection will be cleaned up');
  }, []);
  
  // SSE hook for real-time payment updates
  const sseState = useSecureSSE({
    cartId: cartIdRef.current,
    cartType: 'cart',
    enabled: paymentStatus.status === 'processing',
    onStatusChange: handleStatusUpdate,
    onError: handleSSEError,
    onTimeout: handleSSETimeout
  });

  const calculateCartTotal = useCallback(() => {
    return cartState.items.reduce((total, item) => {
      return total + Number(item.price.amount) * item.qty;
    }, 0);
  }, [cartState.items]);

  const createPayment = useCallback(async () => {
    if (cartState.items.length === 0) {
      setPaymentStatus({ status: "failed", error: "Le panier est vide" });
      return;
    }

    if (!token) {
      setPaymentStatus({
        status: "failed",
        error: "Token d'authentification manquant",
      });
      return;
    }

    setPaymentStatus({ status: "creating" });

    try {
      // 1) Handshake
      await apiService.post(PAYMENT_ENDPOINTS.HANDSHAKE, {}, token);

      // 2) Checkout: créer le panier
      const checkoutData = await apiService.post<CreatePaymentResponse>(
        CART_ENDPOINTS.CHECKOUT(currentBuilding?.pk || 0),
        {
          items: cartState.items.map((item) => ({
            product_id: parseInt(item.id),
            quantity: item.qty,
          })),
        },
        token
      );
      cartIdRef.current = checkoutData.id || null;

      // 3) Créer le lien de paiement
      const payData = await apiService.post<CreatePaymentResponse>(
        PAYMENT_ENDPOINTS.CREATE_PAYMENT(currentBuilding?.pk || 0, "cart"),
        { cart_id: checkoutData.id },
        token
      );

      let paymentUrl =
        payData.payment_url || payData.url || payData.link || payData.short_url;
      if (!paymentUrl && payData.data) {
        paymentUrl =
          payData.data.payment_url ||
          payData.data.url ||
          payData.data.link ||
          payData.data.short_url;
        if (!paymentUrl) {
          try {
            for (const [, v] of Object.entries(payData.data)) {
              if (typeof v === "string" && v.startsWith("http")) {
                paymentUrl = v;
                break;
              }
            }
          } catch {}
        }
      }
      const paymentId = payData.payment_id || payData.id?.toString();
      if (!paymentUrl) {
        throw new Error("URL de paiement absente dans la réponse.");
      }

      const paymentState: PaymentState = {
        status: "processing",
        paymentUrl,
      };
      if (paymentId !== undefined) {
        paymentState.paymentId = paymentId;
      }
      setPaymentStatus(paymentState);

      // Payment URL is now available for popup loading
      // The PaymentModal will handle popup opening and URL monitoring
    } catch (error) {
      console.error("Error creating payment:", error);
      setPaymentStatus({
        status: "failed",
        error: error instanceof Error ? error.message : "Erreur inconnue",
      });
    }
  }, [cartState.items, token, currentBuilding]);

  const cancelPayment = useCallback(() => {
    setPaymentStatus({ status: "cancelled" });
  }, []);

  const resetPayment = useCallback(() => {
    setPaymentStatus({ status: "idle" });
    cartIdRef.current = null;
    stopPopupMonitoring();
  }, [stopPopupMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPopupMonitoring();
    };
  }, [stopPopupMonitoring]);

  const confirmPayment = useCallback(
    async (newStatus: "PAYÉ" | "REFUSÉ" = "PAYÉ") => {
      if (!cartIdRef.current || !currentBuilding || !token) {
        console.error(
          "Cannot confirm payment: missing cart_id, building, or token"
        );
        return;
      }

      try {
        console.log(
          `Confirming payment for cart #${cartIdRef.current} with status ${newStatus}...`
        );

        await apiService.post(
          PAYMENT_ENDPOINTS.CONFIRM_PAYMENT(
            currentBuilding.pk,
            "cart",
            cartIdRef.current
          ),
          { status: newStatus },
          token
        );

        console.log("Payment confirmed");

        if (newStatus === "PAYÉ") {
          setPaymentStatus({ status: "success" });
          clearCart();
        } else if (newStatus === "REFUSÉ") {
          setPaymentStatus({
            status: "failed",
            error: "Paiement refusé. Consultez l'historique pour réessayer.",
          });
          clearCart();
        }
      } catch (error) {
        console.error("Error confirming payment:", error);
      }
    },
    [token, currentBuilding, clearCart]
  );

  const deleteCart = useCallback(async () => {
    if (!cartIdRef.current || !currentBuilding || !token) {
      console.error("Cannot delete cart: missing cart_id, building, or token");
      return;
    }

    try {
      console.log(`Deleting cart #${cartIdRef.current}...`);

      await apiService.delete(
        PAYMENT_ENDPOINTS.DELETE_CART(
          currentBuilding.pk,
          "cart",
          cartIdRef.current
        ),
        token
      );

      console.log("Cart deleted");
    } catch (error) {
      console.error("Error deleting cart:", error);
    }
  }, [token, currentBuilding]);

  const checkPaymentStatus = useCallback(async () => {
    if (!cartIdRef.current || !currentBuilding || !token) {
      return;
    }

    try {
      const statusData = await apiService.get<{ status: string }>(
        PAYMENT_ENDPOINTS.PAYMENT_STATUS(
          currentBuilding.pk,
          "cart",
          cartIdRef.current
        ),
        token
      );

      if (statusData.status === "PAYÉ") {
        console.log("✅ Payment confirmed as PAID");
        setPaymentStatus({ status: "success" });
        clearCart();
        stopPopupMonitoring(); // Stop monitoring when payment is complete
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      } else if (statusData.status === "REFUSÉ") {
        console.log("❌ Payment confirmed as REFUSED");
        setPaymentStatus({ status: "failed", error: "Paiement refusé" });
        clearCart();
        stopPopupMonitoring(); // Stop monitoring when payment is complete
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      } else {
        console.log("⏳ Payment still processing, status:", statusData.status);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  }, [token, currentBuilding, clearCart, stopPopupMonitoring]);

  const detectPaymentResult = useCallback(
    (url: string) => {
      console.log("🔍 Checking payment URL:", url);

      // Success patterns (same as native app)
      if (
        url.includes("success") ||
        url.includes("payment-success") ||
        url.includes("p.success.php") ||
        url.includes("success.php")
      ) {
        console.log("✅ Payment success detected from URL:", url);
        confirmPayment("PAYÉ");
        return true;
      }

      // Failure patterns (same as native app)
      if (
        url.includes("p.fail.php") ||
        url.includes("error") ||
        url.includes("p.error.php") ||
        url.includes("refused") ||
        url.includes("p.refused.php") ||
        url.includes("error.php") ||
        url.includes("refused.php")
      ) {
        console.log("❌ Payment refused detected from URL:", url);
        confirmPayment("REFUSÉ");
        return true;
      }

      // Cancel patterns (same as native app)
      if (
        url.includes("cancel") ||
        url.includes("p.cancel.php") ||
        url.includes("cancel.php")
      ) {
        console.log("🚫 Payment cancelled detected from URL:", url);
        deleteCart();
        return true;
      }

      // Special PayTweak error page
      if (url.includes("payinterim.aspx")) {
        console.log(
          "❌ PayTweak error page detected (payinterim.aspx) - payment failed"
        );
        confirmPayment("REFUSÉ");
        return true;
      }

      return false;
    },
    [confirmPayment, deleteCart]
  );

  const startPopupMonitoring = useCallback(
    (popup: Window) => {
      popupRef.current = popup;
      console.log("🚀 Starting popup monitoring with SSE + URL detection");

      // Lightweight monitoring: URL detection only (SSE handles status updates)
      pollingIntervalRef.current = setInterval(async () => {
        try {
          if (popupRef.current && !popupRef.current.closed) {
            // Strategy 1: URL pattern detection for immediate response
            try {
              const currentUrl = popupRef.current.location.href;
              console.log("🔍 Checking popup URL:", currentUrl);

              if (currentUrl && currentUrl !== "about:blank") {
                const detected = detectPaymentResult(currentUrl);
                if (detected) {
                  console.log("✅ Payment result detected from URL - closing popup");
                  stopPopupMonitoring();
                  popupRef.current.close();
                  return;
                }
              }
            } catch {
              // Cross-origin navigation - payment is processing
              console.log("🔄 Cross-origin navigation - payment in progress (SSE will handle updates)");
            }
            
            // Strategy 2: Fallback status check only if SSE has errors
            if (sseState.error && !sseState.connected) {
              console.log("🔄 SSE not working, checking status as fallback");
              await checkPaymentStatus();
            }
          } else {
            // Popup closed manually - single status check
            console.log("🔍 Popup closed - checking final status");
            await checkPaymentStatus();
            stopPopupMonitoring();
          }
        } catch (error) {
          console.error("❌ Error in popup monitoring:", error);
        }
      }, 3000); // Check every 3 seconds - SSE handles real-time updates
    },
    [checkPaymentStatus, detectPaymentResult, sseState.error, sseState.connected, stopPopupMonitoring]
  );

  return {
    paymentStatus,
    createPayment,
    cancelPayment,
    resetPayment,
    confirmPayment,
    deleteCart,
    checkPaymentStatus,
    startPopupMonitoring,
    stopPopupMonitoring,
    calculateCartTotal,
    cartItemsCount: cartState.items.length,
    cartId: cartIdRef.current,
  };
};
